import styled from "styled-components";
import { useCallback, useEffect, useState } from "react";
import { X, Check, AlertTriangle } from "lucide-react";

import { theme } from "./theme";
import StatusBadge from "./StatusBadge";
import { physicalStatusMeta } from "./status";
import { useT } from "./i18n";
import { formatDate } from "./dateFormat";
import { apiFetch } from "./api";

/* "Pilih kamar" for a line that has NO room (an overbooking, a failed auto-assign). Two steps: pick a
 * room of any type, then read what giving it would do, night by night, and tick that it was read.
 * The server refuses a line that already has a room and refuses the assignment without the tick;
 * moving a line that has a room stays RoomMoveModal's job and is not touched by this one.
 */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(26, 26, 26, 0.5);
  backdrop-filter: blur(2px);
`;

const Panel = styled.div`
  width: 100%;
  max-width: 680px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 14px;
  border-top: 3px solid ${theme.gold};
  box-shadow: 0 24px 60px -18px rgba(26, 26, 26, 0.5);
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.9rem 1rem 0.6rem;
  border-bottom: 1px solid ${theme.border};
`;

const Title = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const Subtitle = styled.div`
  margin-top: 0.15rem;
  font-size: 0.84rem;
  color: ${theme.ink700};
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.85rem 1.1rem;
`;

const Intro = styled.p`
  margin: 0 0 0.7rem;
  font-size: 0.84rem;
  line-height: 1.5;
  color: ${theme.ink700};
`;

const Muted = styled.div`
  padding: 0.6rem 0.25rem;
  font-size: 0.9rem;
  color: ${theme.muted};
`;

const ErrorNote = styled.div`
  margin-bottom: 0.7rem;
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  background: ${theme.dangerBg};
  border: 1px solid ${theme.dangerBorder};
  color: ${theme.danger};
  font-size: 0.85rem;
  font-weight: 600;
`;

const TypeBlock = styled.div`
  margin-bottom: 1rem;
`;

const TypeHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.3rem;
  font-size: 0.86rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const Tag = styled.span`
  flex: 0 0 auto;
  padding: 0.1rem 0.45rem;
  border-radius: 999px;
  background: ${(props) => props.$bg};
  color: ${(props) => props.$fg};
  font-size: 0.75rem;
  font-weight: 800;
`;

const RoomList = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  overflow: hidden;
`;

const RoomRow = styled.button`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
  padding: 0.5rem 0.7rem;
  border: 0;
  border-top: 1px solid ${theme.border};
  background: ${(props) => (props.$selected ? "#e8f5e9" : "#ffffff")};
  box-shadow: ${(props) =>
    props.$selected ? "inset 3px 0 0 #4caf50" : "none"};
  cursor: ${(props) => (props.$selectable ? "pointer" : "default")};
  text-align: left;
  &:first-child {
    border-top: 0;
  }
  &:hover {
    background: ${(props) =>
      props.$selectable && !props.$selected ? "#f6faf6" : undefined};
  }
`;

const RoomCode = styled.div`
  flex: 0 0 3.6rem;
  font-size: 0.95rem;
  font-weight: 800;
  color: ${(props) => (props.$dim ? theme.muted : theme.ink)};
`;

const RoomInfo = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const RoomNote = styled.div`
  font-size: 0.8rem;
  line-height: 1.35;
  color: ${(props) => props.$color || theme.muted};
`;

const TickSlot = styled.span`
  flex: 0 0 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #2e7d32;
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem;
  margin-bottom: 0.8rem;
`;

const SummaryCard = styled.div`
  padding: 0.55rem 0.7rem;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  background: ${(props) => (props.$accent ? theme.goldSoft : "#ffffff")};
`;

const SummaryLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${theme.ink700};
`;

const SummaryValue = styled.div`
  margin-top: 0.15rem;
  font-size: 0.95rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const SummaryDetail = styled.div`
  font-size: 0.8rem;
  color: ${theme.ink700};
`;

const WarningList = styled.ul`
  margin: 0 0 0.8rem;
  padding: 0;
  list-style: none;
`;

const WarningItem = styled.li`
  display: flex;
  gap: 0.45rem;
  margin-bottom: 0.4rem;
  padding: 0.5rem 0.65rem;
  border-radius: 8px;
  background: ${(props) => (props.$info ? "#eef5fd" : "#fff4e5")};
  border: 1px solid ${(props) => (props.$info ? "#c9ddf2" : "#f3d9b1")};
  color: ${(props) => (props.$info ? "#1d4f86" : "#7a4100")};
  font-size: 0.84rem;
  line-height: 1.45;
`;

const NightTable = styled.table`
  width: 100%;
  margin-bottom: 0.8rem;
  border-collapse: collapse;
  font-size: 0.82rem;
  th,
  td {
    padding: 0.35rem 0.5rem;
    border-bottom: 1px solid ${theme.border};
    text-align: left;
  }
  th {
    font-size: 0.75rem;
    font-weight: 800;
    color: ${theme.ink700};
    text-transform: uppercase;
  }
`;

const SystemDetails = styled.details`
  margin-bottom: 0.8rem;
  font-size: 0.8rem;
  color: ${theme.ink700};
  summary {
    cursor: pointer;
    font-weight: 700;
  }
  ul {
    margin: 0.35rem 0 0;
    padding-left: 1.1rem;
  }
`;

const AckBox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  background: ${theme.goldSoft};
  border-left: 4px solid ${theme.goldDark};
  font-size: 0.88rem;
  font-weight: 700;
  line-height: 1.45;
  color: ${theme.ink};
  cursor: pointer;
  input {
    margin-top: 0.2rem;
  }
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 1.1rem;
  border-top: 1px solid ${theme.border};
`;

const Selected = styled.span`
  margin-right: auto;
  font-size: 0.84rem;
  color: ${theme.ink700};
`;

const Cancel = styled.button`
  padding: 0.45rem 1rem;
  border: 1px solid ${theme.border};
  border-radius: 9px;
  background: #ffffff;
  font-size: 0.9rem;
  font-weight: 700;
  color: ${theme.ink700};
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const Confirm = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 1.1rem;
  border: 0;
  border-radius: 9px;
  background: ${theme.ink};
  color: ${theme.gold};
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${theme.inkSoft};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// The planner's per-night cells are English role tags; this is the only place they are shown.
const CELL_KEYS = {
  Source: "assign_room_cell_booking",
  blocked: "assign_room_cell_blocked",
  free: "assign_room_cell_free",
  Displaced: "assign_room_cell_other",
};

const blockText = (block) =>
  [block.reason, block.note].filter(Boolean).join(": ");

const AssignRoomModal = (props) => {
  const { t, lang } = useT();
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const [processing, setProcessing] = useState(false);

  const base = `/frontdesk_v2/bookings/${props.bookingId}`;

  useEffect(() => {
    let active = true;
    const run = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await apiFetch(
          `${base}/assign_targets?change_line_id=${props.changeLineId}`,
        );
        if (!active) {
          return;
        }
        if (res?.data?.room_types) {
          setTargets(res.data);
        } else {
          setError(res?.message || t("error_body"));
        }
      } catch {
        if (active) {
          setError(t("error_body"));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    run();
    return () => {
      active = false;
    };
  }, [base, props.changeLineId, t]);

  const loadPreview = useCallback(
    async (room) => {
      setPreviewLoading(true);
      setError("");
      setAcknowledged(false);
      try {
        const res = await apiFetch(
          `${base}/assign_preview?change_line_id=${props.changeLineId}&target_room_id=${room.id}`,
        );
        if (res?.data?.rows) {
          setPreview(res.data);
          const waits = (res.data.availability_cooldown || []).map(
            (entry) => entry.remaining_seconds || 0,
          );
          setCooldownLeft(waits.length ? Math.max(...waits) : 0);
        } else {
          setError(res?.message || t("error_body"));
          setPreview(null);
        }
      } catch {
        setError(t("error_body"));
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    },
    [base, props.changeLineId, t],
  );

  // The eZee cooldown is a wait, not a refusal: count it down, then ask the server again.
  useEffect(() => {
    if (cooldownLeft <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => {
      if (cooldownLeft === 1 && selected) {
        loadPreview(selected);
      }
      setCooldownLeft(cooldownLeft - 1);
    }, 1000);
    return () => clearTimeout(timer);
  }, [cooldownLeft, selected, loadPreview]);

  const submit = async () => {
    setProcessing(true);
    setError("");
    try {
      const res = await apiFetch(`${base}/assign_room`, {
        method: "POST",
        body: JSON.stringify({
          change_line_id: props.changeLineId,
          target_room_id: selected?.id,
          acknowledged: acknowledged,
          line_id: props.anchorLineId,
        }),
      });
      if (res?.data?.booking) {
        props.onChanged(res.data, res.message);
        props.onClose();
      } else {
        setError(res?.message || t("error_body"));
      }
    } catch {
      setError(t("error_body"));
    } finally {
      setProcessing(false);
    }
  };

  const backToList = () => {
    setPreview(null);
    setAcknowledged(false);
    setCooldownLeft(0);
    setError("");
  };

  const roomNote = (room) => {
    if (!room.eligible) {
      const guest = room.occupant?.guest ? ` (${room.occupant.guest})` : "";
      return (
        <RoomNote>
          {(room.reason || t("change_room_unavailable")) + guest}
        </RoomNote>
      );
    }
    if (room.blocked_nights.length > 0) {
      const reasons = room.blocked_nights.map(blockText).filter(Boolean);
      return (
        <RoomNote $color="#7a4100">
          {room.fully_blocked
            ? t("assign_room_blocked_all")
            : `${t("assign_room_blocked_some")} ${room.blocked_nights.length}/${targets.line.nights} ${t("nights")}`}
          {reasons.length ? ` — ${[...new Set(reasons)].join(", ")}` : ""}
        </RoomNote>
      );
    }
    return <RoomNote $color="#15803d">{t("move_room_mode_free")}</RoomNote>;
  };

  const renderRoom = (room) => {
    const isSelected = selected?.id === room.id;
    return (
      <RoomRow
        key={room.id}
        type="button"
        $selectable={room.eligible}
        $selected={isSelected}
        disabled={!room.eligible}
        onClick={() => setSelected(room)}
      >
        <RoomCode $dim={!room.eligible}>{room.code}</RoomCode>
        <RoomInfo>
          {roomNote(room)}
          {room.transit_overlaps.length > 0 ? (
            <RoomNote $color="#0369a1">{t("move_room_transit_note")}</RoomNote>
          ) : null}
        </RoomInfo>
        {room.physical_status ? (
          <StatusBadge
            meta={physicalStatusMeta(room.physical_status)}
            variant="soft"
            size={12}
          />
        ) : null}
        <TickSlot>{isSelected ? <Check size={14} /> : null}</TickSlot>
      </RoomRow>
    );
  };

  const renderList = () => {
    const types = targets?.room_types || [];
    if (types.length === 0) {
      return <Muted>{t("change_room_none")}</Muted>;
    }
    return types.map((type) => (
      <TypeBlock key={type.id}>
        <TypeHeading>
          {type.name} ({type.code})
          {type.same_type ? (
            <Tag $bg="#e8f5e9" $fg="#1b5e20">
              {t("assign_room_tag_booked_type")}
            </Tag>
          ) : (
            <Tag $bg="#fff4e5" $fg="#7a4100">
              {t("assign_room_tag_other_type")}
            </Tag>
          )}
        </TypeHeading>
        <RoomList>{type.rooms.map(renderRoom)}</RoomList>
      </TypeBlock>
    ));
  };

  const warnings = () => {
    const list = [];
    const room = preview.target_room;
    if (!preview.same_type) {
      list.push(
        `${t("assign_room_warn_cross_type")} ${targets.line.room_type_name} (${targets.line.room_type_code}) → ${room.room_type_name} (${room.room_type_code}).`,
      );
    }
    if (preview.converted_blocks.length > 0) {
      const nights = preview.converted_blocks
        .map((block) => {
          const detail = blockText(block);
          const date = formatDate(block.date, lang);
          return detail ? `${date} (${detail})` : date;
        })
        .join(", ");
      list.push(`${t("assign_room_warn_blocked")} ${nights}.`);
    }
    if (preview.availability_changes.length > 0) {
      const changes = preview.availability_changes
        .map(
          (change) =>
            `${change.room_type_code} ${formatDate(change.date, lang)} (${change.delta > 0 ? "+" : ""}${change.delta})`,
        )
        .join(", ");
      list.push(`${t("assign_room_warn_ezee")} ${changes}.`);
    }
    if (room.physical_status && room.physical_status !== "vacant_clean") {
      list.push(
        `${t("assign_room_warn_physical")} ${t(physicalStatusMeta(room.physical_status).labelKey)}.`,
      );
    }
    (selected?.transit_overlaps || []).forEach((overlap) => {
      list.push(lang === "en" ? overlap.message_en : overlap.message_id);
    });
    return [...new Set(list)];
  };

  const renderPreview = () => {
    const room = preview.target_room;
    const line = targets.line;
    const notes = warnings();
    const cell = (value) => (CELL_KEYS[value] ? t(CELL_KEYS[value]) : value);
    return (
      <>
        <Summary>
          <SummaryCard>
            <SummaryLabel>{t("assign_room_booked")}</SummaryLabel>
            <SummaryValue>{line.room_type_name}</SummaryValue>
            <SummaryDetail>
              {formatDate(line.check_in_date, lang)} –{" "}
              {formatDate(line.check_out_date, lang)} · {line.nights}{" "}
              {t("nights")}
            </SummaryDetail>
          </SummaryCard>
          <SummaryCard $accent>
            <SummaryLabel>{t("assign_room_target")}</SummaryLabel>
            <SummaryValue>{room.code}</SummaryValue>
            <SummaryDetail>{room.room_type_name}</SummaryDetail>
          </SummaryCard>
        </Summary>
        {[...new Set(preview.errors)].map((message) => (
          <ErrorNote key={message}>{message}</ErrorNote>
        ))}
        <WarningList>
          {notes.map((note) => (
            <WarningItem key={note}>
              <AlertTriangle size={15} style={{ flex: "0 0 auto" }} />
              <span>{note}</span>
            </WarningItem>
          ))}
          {preview.availability_changes.length === 0 ? (
            <WarningItem $info>
              <Check size={15} style={{ flex: "0 0 auto" }} />
              <span>{t("assign_room_ezee_unchanged")}</span>
            </WarningItem>
          ) : null}
          {cooldownLeft > 0 ? (
            <WarningItem>
              <AlertTriangle size={15} style={{ flex: "0 0 auto" }} />
              <span>
                {t("assign_room_cooldown")} {cooldownLeft}s
              </span>
            </WarningItem>
          ) : null}
        </WarningList>
        <NightTable>
          <thead>
            <tr>
              <th>{t("assign_room_col_night")}</th>
              <th>
                {room.code} {t("assign_room_col_before")}
              </th>
              <th>
                {room.code} {t("assign_room_col_after")}
              </th>
            </tr>
          </thead>
          <tbody>
            {preview.rows.map((row) => (
              <tr key={row.date}>
                <td>{formatDate(row.date, lang)}</td>
                <td>{cell(row.target_before)}</td>
                <td>{cell(row.target_after)}</td>
              </tr>
            ))}
          </tbody>
        </NightTable>
        {preview.warnings.length > 0 ? (
          <SystemDetails>
            <summary>{t("assign_room_system_notes")}</summary>
            <ul>
              {[...new Set(preview.warnings)].map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </SystemDetails>
        ) : null}
        {preview.can_assign ? (
          <AckBox>
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
            <span>{t("assign_room_ack")}</span>
          </AckBox>
        ) : null}
      </>
    );
  };

  const confirming = Boolean(preview);
  const canSubmit =
    confirming &&
    preview.can_assign &&
    acknowledged &&
    cooldownLeft <= 0 &&
    !processing;

  return (
    <Overlay onClick={props.onClose}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <Head>
          <div>
            <Title>{t("assign_room_title")}</Title>
            {targets?.line ? (
              <Subtitle>
                {targets.line.room_type_name} ·{" "}
                {formatDate(targets.line.check_in_date, lang)} –{" "}
                {formatDate(targets.line.check_out_date, lang)}
              </Subtitle>
            ) : null}
          </div>
          <IconButton
            type="button"
            aria-label={t("close")}
            onClick={props.onClose}
          >
            <X size={16} />
          </IconButton>
        </Head>
        <Body>
          {confirming ? null : <Intro>{t("assign_room_intro")}</Intro>}
          {error ? <ErrorNote>{error}</ErrorNote> : null}
          {loading || previewLoading ? (
            <Muted>{t("loading")}</Muted>
          ) : confirming ? (
            renderPreview()
          ) : targets ? (
            renderList()
          ) : null}
        </Body>
        <Foot>
          <Selected>
            {selected ? `${t("change_room_selected")}: ${selected.code}` : ""}
          </Selected>
          <Cancel
            type="button"
            onClick={() => (confirming ? backToList() : props.onClose())}
          >
            {confirming ? t("assign_room_back") : t("confirm_cancel")}
          </Cancel>
          <Confirm
            type="button"
            disabled={
              confirming ? !canSubmit : !selected || previewLoading || loading
            }
            onClick={() => (confirming ? submit() : loadPreview(selected))}
          >
            <Check size={15} />
            {confirming ? t("assign_room_ok") : t("assign_room_next")}
          </Confirm>
        </Foot>
      </Panel>
    </Overlay>
  );
};

export default AssignRoomModal;
